import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from '../utils/api';

const CYCLE_MS = 5000;
// Refresh on the same cadence as the cycle so the displayed break's data
// (margin, score, sold/total) is always fresh — including the lone-break
// case where the index never advances.
const REFRESH_MS = 5000;

export default function LiveBreakSummary({ onSelectBreak }) {
  const [live, setLive] = useState([]);
  const [index, setIndex] = useState(0);

  const fetchLive = async () => {
    try {
      const r = await api.get('/api/breaks?status=live');
      setLive(r.data?.breaks || []);
    } catch (e) {
      // Silent fail — widget is best-effort; don't disrupt the topbar.
    }
  };

  useEffect(() => {
    fetchLive();
    const t = setInterval(fetchLive, REFRESH_MS);
    return () => clearInterval(t);
  }, []);

  // Auto-cycle when there's more than one live break.
  useEffect(() => {
    if (live.length <= 1) return;
    const t = setInterval(() => setIndex(i => (i + 1) % live.length), CYCLE_MS);
    return () => clearInterval(t);
  }, [live.length]);

  // Clamp index if the list shrinks.
  useEffect(() => {
    if (index >= live.length && live.length > 0) setIndex(0);
  }, [live.length, index]);

  const current = live[index] || live[0];

  const summary = useMemo(() => {
    if (!current) return null;
    // Projected margin — same formula as the Break Spots page (Breaks.jsx
    // line ~717): extrapolate current avg sold price across every total
    // spot, then ((revenue − break_products cogs) / revenue) × 100.
    const sold = Number(current.sold_spots_count || 0);
    const total = Number(current.total_spots || 0);
    const soldSum = Number(current.sold_spots_sum || 0);
    const avgSold = sold > 0 ? soldSum / sold : 0;
    const projectedRevenue = avgSold * total;
    const cogs = Number(current.total_cogs || 0);
    const margin = projectedRevenue > 0 ? ((projectedRevenue - cogs) / projectedRevenue * 100) : null;
    const score = current.score?.score;
    return {
      breakNumber: current.break_number,
      name: current.name,
      host: current.host_name || '—',
      margin: margin == null ? '—' : `${margin.toFixed(1)}%`,
      score: score == null ? '—' : score,
      spots: total > 0 ? `${sold}/${total}` : `${sold}`,
    };
  }, [current]);

  if (!current || !summary) return null;

  const cyclable = live.length > 1;
  const prev = () => setIndex(i => (i - 1 + live.length) % live.length);
  const next = () => setIndex(i => (i + 1) % live.length);

  return (
    <div className="live-break-summary" role="status" aria-label="Live break summary">
      {cyclable && (
        <button className="live-break-arrow" onClick={prev} aria-label="Previous live break">‹</button>
      )}
      <button
        className="live-break-body"
        onClick={() => onSelectBreak?.(current.id)}
        aria-label={`Open break ${summary.breakNumber} details`}
      >
        <span className="live-break-dot" aria-hidden="true" />
        <span className="live-break-field"><strong>#{summary.breakNumber ?? '—'}</strong></span>
        <span className="live-break-field live-break-name">{summary.name}</span>
        <span className="live-break-sep">·</span>
        <span className="live-break-field"><span className="live-break-label">Host</span> {summary.host}</span>
        <span className="live-break-sep">·</span>
        <span className="live-break-field"><span className="live-break-label">Projected Margin</span> {summary.margin}</span>
        <span className="live-break-sep">·</span>
        <span className="live-break-field"><span className="live-break-label">Score</span> {summary.score}</span>
        <span className="live-break-sep">·</span>
        <span className="live-break-field">{summary.spots}</span>
        {cyclable && <span className="live-break-counter">{index + 1}/{live.length}</span>}
      </button>
      {cyclable && (
        <button className="live-break-arrow" onClick={next} aria-label="Next live break">›</button>
      )}
    </div>
  );
}
